<template>
  <div class="space-y-2">
    <div class="flex items-center gap-2">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ name }}
        <span v-if="required" class="text-red-500">*</span>
      </label>
      <Badge v-if="showSuggested" variant="info" size="sm">Suggested</Badge>
    </div>

    <p v-if="schema.description" class="text-xs text-gray-500 dark:text-gray-400">
      {{ schema.description }}
    </p>

    <Input
      v-if="schema.type === 'string' && !schema.enum"
      :model-value="inputValue ?? ''"
      :placeholder="placeholder"
      @update:model-value="value => emitUpdate(value, true)"
    />

    <Select
      v-else-if="schema.type === 'string' && schema.enum"
      :model-value="inputValue ?? ''"
      :options="enumOptions"
      placeholder="Select an option"
      @update:model-value="value => emitUpdate(value, true)"
    />

    <Input
      v-else-if="schema.type === 'number' || schema.type === 'integer'"
      type="number"
      :model-value="inputValue ?? ''"
      :min="minVal"
      :max="schema.maximum != null ? String(schema.maximum) : undefined"
      :placeholder="placeholder"
      @update:model-value="value => emitUpdate(value, true)"
    />

    <div v-else-if="schema.type === 'boolean'" class="flex items-center">
      <input
        type="checkbox"
        :checked="modelValue === true"
        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        @change="handleBooleanChange"
      />
      <label class="ml-2 text-sm text-gray-700 dark:text-gray-300">Enable</label>
    </div>

    <div v-else-if="schema.type === 'object'" class="space-y-4">
      <div v-if="!hasObjectProperties" class="text-xs text-gray-500 dark:text-gray-400">
        No properties defined
      </div>
      <div v-else class="space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <SchemaField
          v-for="(childSchema, childName) in schema.properties"
          :key="childName"
          :name="childName"
          :schema="childSchema"
          :model-value="getChildValue(childName)"
          :required="isChildRequired(childName)"
          :path="childPath(childName)"
          :errors="errors"
          :suggestions="suggestions"
          :touched="touched"
          @update="emitChildUpdate"
        />
      </div>
    </div>

    <div v-else-if="schema.type === 'array'" class="space-y-3">
      <div v-if="arrayItems.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
        No items added
      </div>
      <div v-for="(item, index) in arrayItems" :key="index" class="space-y-2">
        <SchemaField
          v-if="schema.items"
          :name="`${name}[${index}]`"
          :schema="schema.items"
          :model-value="item"
          :required="required"
          :path="`${path}[${index}]`"
          :errors="errors"
          :suggestions="suggestions"
          :touched="touched"
          @update="emitChildUpdate"
        />
        <Button variant="ghost" size="sm" class="text-red-600" @click="removeItem(index)">
          Remove
        </Button>
      </div>
      <Button variant="outline" size="sm" @click="addItem">Add Item</Button>
    </div>

    <Textarea
      v-else
      :model-value="String(modelValue ?? '')"
      :placeholder="placeholder"
      @update:model-value="value => emitUpdate(value, true)"
    />

    <p v-if="errorMessage" class="text-xs text-red-600 dark:text-red-400">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { Badge, Button, Input, Select, Textarea } from '@asabdev/asab-ui';
  import type { JsonSchemaProperty } from '../../types/mcp';
  import { getDefaultValue } from '../../lib/mcp-schema';

  interface Props {
    name: string;
    schema: JsonSchemaProperty;
    modelValue: unknown;
    required?: boolean;
    path: string;
    errors: Map<string, string>;
    suggestions: Map<string, unknown>;
    touched: Set<string>;
  }

  interface Emits {
    (e: 'update', path: string, value: unknown, userInitiated: boolean): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  defineOptions({ name: 'SchemaField' });

  const inputValue = computed(() => props.modelValue as string | number | undefined);
  const minVal = computed(() => (props.schema.minimum != null ? String(props.schema.minimum) : undefined));

  const placeholder = computed((): string => {
    if (props.schema.default !== undefined) {
      return `Default: ${props.schema.default}`;
    }
    if (props.schema.type === 'string') return 'Enter text...';
    if (props.schema.type === 'number' || props.schema.type === 'integer') return 'Enter number...';
    if (props.schema.type === 'array') return 'Add items...';
    if (props.schema.type === 'object') return 'Enter object...';
    return '';
  });

  const hasObjectProperties = computed(() => {
    return props.schema.type === 'object' && !!props.schema.properties;
  });

  const arrayItems = computed(() => {
    if (!Array.isArray(props.modelValue)) return [];
    return props.modelValue as unknown[];
  });

  const errorMessage = computed(() => props.errors.get(props.path) || '');

  const enumOptions = computed(() => {
    if (!props.schema.enum) return [];
    return props.schema.enum.map(opt => ({ value: String(opt), label: String(opt) }));
  });

  const showSuggested = computed(() => {
    return props.suggestions.has(props.path) && !props.touched.has(props.path);
  });

  const emitUpdate = (value: unknown, userInitiated: boolean) => {
    emit('update', props.path, value, userInitiated);
  };

  const handleBooleanChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    emitUpdate(target.checked, true);
  };

  const childPath = (childName: string): string => {
    return props.path ? `${props.path}.${childName}` : childName;
  };

  const isChildRequired = (childName: string): boolean => {
    return props.schema.required?.includes(childName) ?? false;
  };

  const getChildValue = (childName: string): unknown => {
    if (props.modelValue && typeof props.modelValue === 'object' && !Array.isArray(props.modelValue)) {
      return (props.modelValue as Record<string, unknown>)[childName];
    }
    return undefined;
  };

  const emitChildUpdate = (path: string, value: unknown, userInitiated: boolean) => {
    emit('update', path, value, userInitiated);
  };

  const addItem = () => {
    const items = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const defaultValue = props.schema.items ? getDefaultValue(props.schema.items) : undefined;
    items.push(defaultValue ?? null);
    emitUpdate(items, true);
  };

  const removeItem = (index: number) => {
    const items = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    items.splice(index, 1);
    emitUpdate(items, true);
  };
</script>
